import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Stack,
  Typography,
  TextField,
  Button,
  Checkbox,
  InputAdornment,
  Avatar,
  Divider,
  Skeleton,
  Alert,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { api } from "../../utils/api";

export default function ClientAssignBulkServices() {
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [selClients, setSelClients] = useState([]);
  const [selServices, setSelServices] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [clRes, svRes] = await Promise.all([
          api.get("/clients/list.php?page=1&pageSize=1000"),
          api.get("/services/list.php"),
        ]);
        setClients(clRes.clients || []);
        setServices(
          (svRes.services || []).map((s) => ({ ...s, price: s.default_price })),
        );
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleClient = (id) =>
    setSelClients((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleService = (id) =>
    setSelServices((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const filteredClients = clients.filter((c) =>
    JSON.stringify(c).toLowerCase().includes(clientSearch.toLowerCase()),
  );
  const filteredServices = services.filter((s) =>
    JSON.stringify(s).toLowerCase().includes(serviceSearch.toLowerCase()),
  );

  const allClientsSel =
    filteredClients.length > 0 &&
    filteredClients.every((c) => selClients.includes(c.id));
  const allServicesSel =
    filteredServices.length > 0 &&
    filteredServices.every((s) => selServices.includes(s.id));

  const updatePrice = (id, val) =>
    setServices((old) => old.map((s) => (s.id === id ? { ...s, price: val } : s)));

  const save = async () => {
    setMsg(null);
    if (!selClients.length || !selServices.length) {
      setMsg({ type: "warning", text: "Select at least one client and service" });
      return;
    }
    setSaving(true);
    try {
      const payload = selClients.map((clientId) => ({
        client_id: clientId,
        services: services
          .filter((s) => selServices.includes(s.id))
          .map((s) => ({ service_id: s.id, price: s.price, is_active: 1 })),
      }));
      await api.post("/clients/bulk_assign.php", payload);
      setMsg({ type: "success", text: "Services assigned successfully" });
      setSelClients([]);
      setSelServices([]);
    } catch (err) {
      setMsg({ type: "error", text: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: "warning.main", width: 48, height: 48 }}>
            <BoltIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Bulk Assign Services
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Assign services to multiple clients with custom pricing
            </Typography>
          </Box>
        </Stack>
        <Button
          startIcon={<RestartAltIcon />}
          variant="outlined"
          onClick={() => {
            setSelClients([]);
            setSelServices([]);
          }}
        >
          Reset
        </Button>
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }}>
          {msg.text}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ height: 540, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                p: 1.5,
                borderBottom: 1,
                borderColor: "divider",
                fontWeight: 700,
              }}
            >
              Clients {selClients.length > 0 && `(${selClients.length} selected)`}
            </Box>
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search clients"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                size="small"
                onClick={() =>
                  allClientsSel
                    ? setSelClients([])
                    : setSelClients(filteredClients.map((c) => c.id))
                }
                sx={{ mt: 1 }}
              >
                {allClientsSel ? "Deselect All" : "Select All"}
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {loading &&
                [...Array(6)].map((_, i) => (
                  <Box key={i} sx={{ p: 1 }}>
                    <Skeleton height={48} />
                  </Box>
                ))}
              {!loading &&
                filteredClients.map((c) => (
                  <Box
                    key={c.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderBottom: 1,
                      borderColor: "divider",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                    onClick={() => toggleClient(c.id)}
                  >
                    <Checkbox
                      size="small"
                      checked={selClients.includes(c.id)}
                      onChange={() => toggleClient(c.id)}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.business_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.business_type} · {c.mobile}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.disabled">
                      PAN: {c.pan || "—"}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ height: 540, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                p: 1.5,
                borderBottom: 1,
                borderColor: "divider",
                fontWeight: 700,
              }}
            >
              Services {selServices.length > 0 && `(${selServices.length} selected)`}
            </Box>
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search services"
                value={serviceSearch}
                onChange={(e) => setServiceSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                size="small"
                onClick={() =>
                  allServicesSel
                    ? setSelServices([])
                    : setSelServices(filteredServices.map((s) => s.id))
                }
                sx={{ mt: 1 }}
              >
                {allServicesSel ? "Deselect All" : "Select All"}
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {loading &&
                [...Array(6)].map((_, i) => (
                  <Box key={i} sx={{ p: 1 }}>
                    <Skeleton height={48} />
                  </Box>
                ))}
              {!loading &&
                filteredServices.map((s) => (
                  <Box
                    key={s.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selServices.includes(s.id)}
                      onChange={() => toggleService(s.id)}
                    />
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                      {s.name}
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={s.price}
                      onChange={(e) => updatePrice(s.id, e.target.value)}
                      sx={{ width: 100 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Saving…" : "Assign Services"}
        </Button>
      </Stack>
    </Box>
  );
}
